import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Eye,
  CheckCircle,
  Edit,
  Download,
  Clock,
  User,
  Monitor,
  Smartphone,
  Globe,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContractAuditLogPage = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();

  const [contract, setContract] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContract();
    fetchAuditLog();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/${contractId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContract(response.data);
    } catch (err) {
      console.error('خطأ في جلب العقد:', err);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/signatures/contract/${contractId}/audit-log`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuditLog(response.data.audit_log || []);
    } catch (err) {
      console.error('خطأ في جلب سجل التدقيق:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'signed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'viewed':
        return <Eye className="w-5 h-5 text-blue-600" />;
      case 'status_updated':
        return <Edit className="w-5 h-5 text-orange-600" />;
      case 'downloaded':
        return <Download className="w-5 h-5 text-purple-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'signed':
        return 'bg-green-50 border-green-200';
      case 'viewed':
        return 'bg-blue-50 border-blue-200';
      case 'status_updated':
        return 'bg-orange-50 border-orange-200';
      case 'downloaded':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getActionText = (action) => {
    const actions = {
      'signed': 'توقيع',
      'viewed': 'عرض',
      'status_updated': 'تحديث الحالة',
      'downloaded': 'تحميل'
    };
    return actions[action] || action;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8A870] mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#C8A870] to-[#b8986a] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1e3a8a]">سجل التدقيق</h1>
                <p className="text-gray-600">رقم العقد: {contract?.contract_number}</p>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/admin/contracts')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowRight className="w-5 h-5" />
              رجوع
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">إجمالي الأحداث</p>
                <p className="text-2xl font-bold text-gray-900">{auditLog.length}</p>
              </div>
              <Clock className="w-10 h-10 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">التوقيعات</p>
                <p className="text-2xl font-bold text-green-600">
                  {auditLog.filter(log => log.action === 'signed').length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">المشاهدات</p>
                <p className="text-2xl font-bold text-blue-600">
                  {auditLog.filter(log => log.action === 'viewed').length}
                </p>
              </div>
              <Eye className="w-10 h-10 text-blue-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">التحميلات</p>
                <p className="text-2xl font-bold text-purple-600">
                  {auditLog.filter(log => log.action === 'downloaded').length}
                </p>
              </div>
              <Download className="w-10 h-10 text-purple-400" />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-gray-900 mb-6">سجل الأحداث التفصيلي</h3>

          {auditLog.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>لا توجد أحداث مسجلة بعد</p>
            </div>
          ) : (
            <div className="space-y-4">
              {auditLog.map((entry, index) => (
                <div
                  key={entry.id}
                  className={`border-2 rounded-lg p-4 ${getActionColor(entry.action)}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {getActionIcon(entry.action)}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-bold text-gray-900">
                            {getActionText(entry.action)}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {entry.details || 'لا توجد تفاصيل إضافية'}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap mr-4">
                          {formatDate(entry.timestamp)}
                        </span>
                      </div>

                      {/* User & Device Info */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{entry.user_name}</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-600">
                          {entry.device_type === 'mobile' ? (
                            <Smartphone className="w-4 h-4" />
                          ) : (
                            <Monitor className="w-4 h-4" />
                          )}
                          <span>{entry.device_type === 'mobile' ? 'جوال' : 'حاسوب'}</span>
                        </div>

                        {entry.ip_address && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Globe className="w-4 h-4" />
                            <span className="font-mono text-xs">{entry.ip_address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractAuditLogPage;
